import React from 'react';

import { Link } from '~/lib/react-router-dom';

import {
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
} from '@mui/material';

import { useGetPricingToolDraft } from '~/hooks/apis/offers/usePricingToolDraft';
import { useGetLastConfirmedPricingToolSubmission } from '~/hooks/apis/offers/usePricingToolSubmissions';

import { formatDateShortWithClock, isAfter } from '~/services/TimeService';

interface Props {
  offerId: string;
  vendorAccountId: string;
  open: boolean;
  onClose: () => void;
}

export default function SetUpFlashOfferDialog({ offerId, vendorAccountId, open, onClose }: Props) {
  const { data: lastConfirmed } = useGetLastConfirmedPricingToolSubmission(offerId);
  const { data: draft } = useGetPricingToolDraft(offerId);
  // Relies on submit saving the draft first: a later save means unsubmitted changes.
  // Unsaved Pricing Tool edits aren't detected; that needs per-tab dirty tracking.
  const hasUnsubmittedChanges = !!lastConfirmed && !!draft && isAfter(draft.updated_at, lastConfirmed.created_at);

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose} aria-labelledby="set-up-flash-offer-dialog-title">
      <DialogTitle id="set-up-flash-offer-dialog-title">Set up Flash offer</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <DialogContentText>The other tabs unlock once the Flash offer is set up.</DialogContentText>
          {lastConfirmed ? (
            <Alert severity={hasUnsubmittedChanges ? 'warning' : 'success'}>
              <AlertTitle>
                {hasUnsubmittedChanges ? 'Changes not yet submitted' : 'Submitted from the Pricing Tool'}
              </AlertTitle>
              {hasUnsubmittedChanges
                ? `The Pricing Tool has changes saved after the last submission on ${formatDateShortWithClock(lastConfirmed.created_at)}. Resubmit to send them to Salesforce.`
                : `Salesforce was populated on ${formatDateShortWithClock(lastConfirmed.created_at)}.`}
            </Alert>
          ) : (
            <>
              <Alert severity="warning">
                <AlertTitle>No Pricing Tool submission yet</AlertTitle>
                Submit from the Pricing Tool first, unless Salesforce was populated by hand.
              </Alert>
              <DialogContentText color="text.primary">Is Salesforce populated with this deal?</DialogContentText>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>
          {lastConfirmed ? 'Cancel' : 'Not yet'}
        </Button>
        <Button
          variant="contained"
          component={Link}
          to={`/vendors/${vendorAccountId}?setupFlashOffer=${offerId}`}
          onClick={onClose}
        >
          {lastConfirmed ? 'Set up Flash offer' : 'Yes, set up Flash offer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
